import React from 'react';
import { IoSearch, IoGlobe, IoEye } from 'react-icons/io5';
import styles from '../AddProduct.module.css';

function SEOPreview({
    productName,
    metaTitle,
    metaDescription,
    slug,
    focusKeywords,
    brandName,
    availability,
    productCondition,
}) {
    // Generate the final SEO title (meta title or fallback to product name)
    const seoTitle = metaTitle || productName || 'Product Title';

    // Generate the final SEO description (meta description or fallback)
    const seoDescription =
        metaDescription || 'Product description will appear here...';

    // Generate the final URL
    const seoUrl = slug
        ? `https://yoursite.com/products/${slug}`
        : 'https://yoursite.com/products/product-url-slug';

    // Generate brand and condition info
    const brandInfo = brandName
        ? `Brand: ${brandName}`
        : 'Brand: Not specified';
    const conditionInfo =
        productCondition === 'new'
            ? 'New'
            : productCondition === 'used'
            ? 'Used'
            : 'Refurbished';
    const availabilityInfo =
        availability === 'in_stock'
            ? 'In Stock'
            : availability === 'out_of_stock'
            ? 'Out of Stock'
            : 'Pre-order';

    return (
        <div className={styles['AddProduct-card']}>
            <div className={styles['AddProduct-card-header']}>
                <div className={styles['AddProduct-card-title']}>
                    <IoEye className={styles['AddProduct-card-icon']} />
                    <span>SEO Preview</span>
                </div>
                <div className={styles['AddProduct-card-subtitle']}>
                    How your product will appear in search engines
                </div>
            </div>

            <div className={styles['seo-preview-container']}>
                {/* Google Search Result Preview */}
                <div className={styles['seo-preview-section']}>
                    <div className={styles['seo-preview-label']}>
                        <IoSearch className={styles['seo-preview-icon']} />
                        <span>Google Search Result</span>
                    </div>

                    <div className={styles['seo-preview-result']}>
                        <div className={styles['seo-preview-url']}>
                            {seoUrl}
                        </div>
                        <div className={styles['seo-preview-title']}>
                            {seoTitle}
                        </div>
                        <div className={styles['seo-preview-description']}>
                            {seoDescription}
                        </div>
                        <div className={styles['seo-preview-meta']}>
                            <span className={styles['seo-preview-date']}>
                                2 days ago
                            </span>
                            <span className={styles['seo-preview-separator']}>
                                •
                            </span>
                            <span className={styles['seo-preview-status']}>
                                {availabilityInfo}
                            </span>
                            <span className={styles['seo-preview-separator']}>
                                •
                            </span>
                            <span className={styles['seo-preview-condition']}>
                                {conditionInfo}
                            </span>
                        </div>
                    </div>
                </div>

                {/* SEO Analysis */}
                <div className={styles['seo-analysis-section']}>
                    <div className={styles['seo-analysis-label']}>
                        <IoGlobe className={styles['seo-preview-icon']} />
                        <span>SEO Analysis</span>
                    </div>

                    <div className={styles['seo-analysis-grid']}>
                        <div className={styles['seo-analysis-item']}>
                            <div className={styles['seo-analysis-title']}>
                                Title Length
                            </div>
                            <div
                                className={`${styles['seo-analysis-value']} ${
                                    seoTitle.length >= 50 &&
                                    seoTitle.length <= 60
                                        ? styles['seo-analysis-good']
                                        : styles['seo-analysis-warning']
                                }`}
                            >
                                {seoTitle.length}/60 characters
                            </div>
                            <div className={styles['seo-analysis-tip']}>
                                {seoTitle.length < 50
                                    ? 'Consider adding more descriptive words'
                                    : seoTitle.length > 60
                                    ? 'Title might be too long for search results'
                                    : 'Perfect length for search engines'}
                            </div>
                        </div>

                        <div className={styles['seo-analysis-item']}>
                            <div className={styles['seo-analysis-title']}>
                                Description Length
                            </div>
                            <div
                                className={`${styles['seo-analysis-value']} ${
                                    seoDescription.length >= 150 &&
                                    seoDescription.length <= 160
                                        ? styles['seo-analysis-good']
                                        : styles['seo-analysis-warning']
                                }`}
                            >
                                {seoDescription.length}/160 characters
                            </div>
                            <div className={styles['seo-analysis-tip']}>
                                {seoDescription.length < 150
                                    ? 'Add more details about your product'
                                    : seoDescription.length > 160
                                    ? 'Description might be cut off in search results'
                                    : 'Optimal length for search engines'}
                            </div>
                        </div>

                        <div className={styles['seo-analysis-item']}>
                            <div className={styles['seo-analysis-title']}>
                                Keywords
                            </div>
                            <div
                                className={`${styles['seo-analysis-value']} ${
                                    focusKeywords.length >= 3 &&
                                    focusKeywords.length <= 5
                                        ? styles['seo-analysis-good']
                                        : styles['seo-analysis-warning']
                                }`}
                            >
                                {focusKeywords.length} keywords added
                            </div>
                            <div className={styles['seo-analysis-tip']}>
                                {focusKeywords.length === 0
                                    ? 'Add 3-5 relevant keywords for better SEO'
                                    : focusKeywords.length < 3
                                    ? 'Consider adding more keywords'
                                    : focusKeywords.length > 5
                                    ? 'Too many keywords might hurt SEO'
                                    : 'Good keyword density'}
                            </div>
                        </div>

                        <div className={styles['seo-analysis-item']}>
                            <div className={styles['seo-analysis-title']}>
                                URL Structure
                            </div>
                            <div
                                className={`${styles['seo-analysis-value']} ${
                                    slug && slug.length > 0
                                        ? styles['seo-analysis-good']
                                        : styles['seo-analysis-warning']
                                }`}
                            >
                                {slug ? 'Custom URL set' : 'Auto-generated URL'}
                            </div>
                            <div className={styles['seo-analysis-tip']}>
                                {slug
                                    ? 'Custom URL is SEO-friendly'
                                    : 'URL will be auto-generated from product title'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Keywords Display */}
                {focusKeywords.length > 0 && (
                    <div className={styles['seo-keywords-section']}>
                        <div className={styles['seo-keywords-label']}>
                            Focus Keywords
                        </div>
                        <div className={styles['seo-keywords-list']}>
                            {focusKeywords.map((keyword, index) => (
                                <span
                                    key={index}
                                    className={styles['seo-keyword-tag']}
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Additional Info */}
                <div className={styles['seo-info-section']}>
                    <div className={styles['seo-info-item']}>
                        <strong>Brand:</strong> {brandInfo}
                    </div>
                    <div className={styles['seo-info-item']}>
                        <strong>Condition:</strong> {conditionInfo}
                    </div>
                    <div className={styles['seo-info-item']}>
                        <strong>Availability:</strong> {availabilityInfo}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SEOPreview;
